import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Creation, Dimension, FulfillmentsData, RelationTarget, SyntaxchecksData } from "./AppTypes";
import { notifyProjectActivity } from "./api/pingClient";
import { getFulfillmentPalette, getFulfillmentIndicator, resolveFulfillmentTone, FULFILLMENT_STEPS, getBaseColor, Mode } from "./colorUtils";

const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

type Props = {
  creations: Creation[];
  onChange: (creations: Creation[]) => void;
  dimensions: Dimension[];
  allNames: string[];
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  projectId: number;
  syntaxData?: SyntaxchecksData | null;
  statsData?: FulfillmentsData | null;
};

type FlatItem = {
  type: "tag" | "point" | "dimension";
  id: string;
  name: string;
  dimId: string;
};

function buildFlatItems(targets: RelationTarget[], dimensions: Dimension[]): FlatItem[] {
  const items: FlatItem[] = [];
  targets.forEach((target) => {
    const dim = dimensions.find((d) => d.id === target.dimensionId);
    if (!dim) return;

    if (target.mode === "all") {
      if (!items.some((x) => x.type === "dimension" && x.dimId === dim.id)) {
        items.push({ type: "dimension", id: dim.id, name: dim.name, dimId: dim.id });
      }
    } else {
      target.tagIds?.forEach((tagId) => {
        const tag = dim.tags.find((t) => t.id === tagId);
        if (tag && !items.some((x) => x.type === "tag" && x.id === tag.id)) {
          items.push({ type: "tag", id: tag.id, name: tag.name, dimId: dim.id });
        }
      });
      target.pointIds?.forEach((pointId) => {
        const point = dim.points.find((p) => p.id === pointId);
        if (point && !items.some((x) => x.type === "point" && x.id === point.id)) {
          items.push({ type: "point", id: point.id, name: point.name, dimId: dim.id });
        }
      });
    }
  });
  return items;
}

function rebuildTargets(items: FlatItem[]): RelationTarget[] {
  const targets: RelationTarget[] = [];
  items.forEach((item) => {
    if (item.type === "dimension") {
      targets.push({
        dimensionId: item.dimId,
        mode: "all",
        tagIds: [],
        pointIds: [],
      });
      return;
    }

    let existing = targets.find((t) => t.dimensionId === item.dimId && t.mode !== "all");
    if (!existing) {
      existing = { dimensionId: item.dimId, mode: "points", tagIds: [], pointIds: [] };
      targets.push(existing);
    }

    if (item.type === "tag" && !existing.tagIds.includes(item.id)) {
      existing.tagIds.push(item.id);
      existing.mode = existing.pointIds.length === 0 ? "tags" : "points";
    } else if (item.type === "point" && !existing.pointIds.includes(item.id)) {
      existing.pointIds.push(item.id);
      existing.mode = "points";
    }
  });
  return targets;
}

function SortableCreationCard({
  creation,
  expandedId,
  setExpandedId,
  onUpdate,
  onDelete,
  dimensions,
  userHue,
  mode,
  allNames,
  syntaxStat,
  fulfillmentStat,
}: {
  creation: Creation;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<Creation>) => void;
  onDelete: (id: string) => void;
  dimensions: Dimension[];
  userHue: number;
  mode: Mode;
  allNames: string[];
  syntaxStat?: { syntax?: string; SyntaxMessages?: string[] };
  fulfillmentStat?: { Fulfillment?: number; violations?: any[] } | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: creation.id });

  const fulfillment = fulfillmentStat?.Fulfillment;
  const cardBg = (() => {
    if (syntaxStat?.SyntaxMessages && syntaxStat.SyntaxMessages.length > 0) {
      return mode === "dark" ? "hsl(210, 50%, 30%)" : "hsl(210, 95%, 82%)";
    }
    if (typeof fulfillment === "number" && !Number.isNaN(fulfillment)) {
      return getFulfillmentPalette(resolveFulfillmentTone(fulfillment).tone as any, mode).background;
    }
    return getBaseColor(4, "connection", userHue, mode);
  })();

  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: cardBg,
  };

  const expanded = expandedId === creation.id;
  const isDuplicate = allNames.filter((n) => n === creation.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [isHoveringFulfillment, setIsHoveringFulfillment] = useState(false);
  const indicatorRef = useRef<HTMLSpanElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const backendErrors = syntaxStat?.SyntaxMessages;
  const hasBackendError = backendErrors && backendErrors.length > 0;
  const errorMessage = hasBackendError
    ? (Array.isArray(backendErrors) ? backendErrors.map(e => String(e)).join(", ") : String(backendErrors))
    : null;
  const isValid = !errorMessage;
  const validationIcon = !isValid && errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";

  const indicator = getFulfillmentIndicator(fulfillmentStat?.Fulfillment, mode);

  const makeItems = buildFlatItems(creation.make || [], dimensions);
  const forItems = buildFlatItems(creation.for || [], dimensions);

  // Wird während eines Drags gesetzt: true = Drop landete in einer gültigen Zone
  const droppedInZoneRef = useRef(false);
  // Globaler dragover-Handler: sorgt dafür dass onDragEnd sofort feuert (macOS-Fix)
  const globalDragOverRef = useRef<((e: DragEvent) => void) | null>(null);

  const pillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    background: mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    borderRadius: 14,
    fontSize: "14px",
    fontWeight: 500,
    color: mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
    cursor: "grab",
    userSelect: "none",
    transition: "all 0.15s ease",
  };

  const removeDimension = (field: "make" | "for", dimId: string) => {
    onUpdate(creation.id, { [field]: creation[field].filter((t) => t.dimensionId !== dimId) });
  };

  const removeTag = (field: "make" | "for", tagId: string) => {
    const next = creation[field]
      .map((t) => {
        if (t.tagIds?.includes(tagId)) {
          return { ...t, tagIds: t.tagIds.filter((id) => id !== tagId) };
        }
        return t;
      })
      .filter((t) => t.mode === "all" || t.tagIds.length > 0 || t.pointIds.length > 0);
    onUpdate(creation.id, { [field]: next });
  };

  const removePoint = (field: "make" | "for", pointId: string) => {
    const next = creation[field]
      .map((t) => {
        if (t.pointIds?.includes(pointId)) {
          return { ...t, pointIds: t.pointIds.filter((id) => id !== pointId) };
        }
        return t;
      })
      .filter((t) => t.mode === "all" || t.tagIds.length > 0 || t.pointIds.length > 0);
    onUpdate(creation.id, { [field]: next });
  };

  const removeItem = (field: "make" | "for", item: FlatItem) => {
    if (item.type === "dimension") removeDimension(field, item.dimId);
    else if (item.type === "tag") removeTag(field, item.id);
    else removePoint(field, item.id);
  };

  const addToTarget = (field: "make" | "for", dimId: string, type: "tag" | "point", id: string) => {
    const targets = creation[field];
    const existingIdx = targets.findIndex((t) => t.dimensionId === dimId);

    if (existingIdx !== -1) {
      const newTargets = targets.map((target, i) => {
        if (i !== existingIdx) return target;
        if (type === "point") {
          const pointIds = target.mode === "points" ? target.pointIds : [];
          return {
            ...target,
            mode: "points" as const,
            pointIds: pointIds.includes(id) ? pointIds : [...pointIds, id],
            tagIds: [],
          };
        }
        const tagIds = target.mode === "tags" ? target.tagIds : [];
        return {
          ...target,
          mode: "tags" as const,
          tagIds: tagIds.includes(id) ? tagIds : [...tagIds, id],
          pointIds: [],
        };
      });
      onUpdate(creation.id, { [field]: newTargets });
      return;
    }

    onUpdate(creation.id, {
      [field]: [
        ...targets,
        {
          dimensionId: dimId,
          mode: type === "tag" ? "tags" : "points",
          tagIds: type === "tag" ? [id] : [],
          pointIds: type === "point" ? [id] : [],
        },
      ],
    });
  };

  const addDimension = (field: "make" | "for", dimId: string) => {
    const exists = creation[field].some((t) => t.dimensionId === dimId);
    if (!exists) {
      onUpdate(creation.id, {
        [field]: [...creation[field], { dimensionId: dimId, mode: "all", tagIds: [], pointIds: [] }],
      });
      return;
    }

    onUpdate(creation.id, {
      [field]: creation[field].map((t) =>
        t.dimensionId !== dimId ? t : { ...t, mode: "all", tagIds: [], pointIds: [] }
      ),
    });
  };

  const reorderItem = (field: "make" | "for", dragged: FlatItem, target: FlatItem) => {
    const items = buildFlatItems(creation[field], dimensions);
    const dragIdx = items.findIndex((i) => i.type === dragged.type && i.id === dragged.id);
    const targetIdx = items.findIndex((i) => i.type === target.type && i.id === target.id);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;

    const reordered = [...items];
    if (dragged.dimId !== target.dimId) {
      const draggedItems = reordered.filter((i) => i.dimId === dragged.dimId);
      const remaining = reordered.filter((i) => i.dimId !== dragged.dimId);
      const removedBeforeTarget = reordered.slice(0, targetIdx).filter((i) => i.dimId === dragged.dimId).length;
      remaining.splice(Math.max(0, targetIdx - removedBeforeTarget), 0, ...draggedItems);
      onUpdate(creation.id, { [field]: rebuildTargets(remaining) });
      return;
    }

    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    onUpdate(creation.id, { [field]: rebuildTargets(reordered) });
  };

  const DropZone = ({ label, field, items }: { label: string; field: "make" | "for"; items: FlatItem[] }) => {
    const otherField: "make" | "for" = field === "make" ? "for" : "make";

    const moveToThisField = (draggedItem: FlatItem) => {
      // Quell-Feld: Item entfernen
      const computeRemoved = (targets: RelationTarget[]): RelationTarget[] => {
        if (draggedItem.type === "dimension") {
          return targets.filter((t) => t.dimensionId !== draggedItem.dimId);
        }
        if (draggedItem.type === "tag") {
          return targets
            .map((t) => ({ ...t, tagIds: t.tagIds.filter((id) => id !== draggedItem.id) }))
            .filter((t) => t.mode === "all" || t.tagIds.length > 0 || t.pointIds.length > 0);
        }
        // point
        return targets
          .map((t) => ({ ...t, pointIds: t.pointIds.filter((id) => id !== draggedItem.id) }))
          .filter((t) => t.mode === "all" || t.tagIds.length > 0 || t.pointIds.length > 0);
      };

      // Ziel-Feld: Item hinzufügen
      const computeAdded = (targets: RelationTarget[]): RelationTarget[] => {
        if (draggedItem.type === "dimension") {
          const exists = targets.some((t) => t.dimensionId === draggedItem.dimId);
          if (!exists) return [...targets, { dimensionId: draggedItem.dimId, mode: "all", tagIds: [], pointIds: [] }];
          return targets.map((t) => t.dimensionId !== draggedItem.dimId ? t : { ...t, mode: "all", tagIds: [], pointIds: [] });
        }
        const existingIdx = targets.findIndex((t) => t.dimensionId === draggedItem.dimId);
        if (existingIdx !== -1) {
          return targets.map((t, i) => {
            if (i !== existingIdx) return t;
            if (draggedItem.type === "point") {
              const pointIds = t.mode === "points" ? t.pointIds : [];
              return { ...t, mode: "points" as const, pointIds: pointIds.includes(draggedItem.id) ? pointIds : [...pointIds, draggedItem.id], tagIds: [] };
            }
            const tagIds = t.mode === "tags" ? t.tagIds : [];
            return { ...t, mode: "tags" as const, tagIds: tagIds.includes(draggedItem.id) ? tagIds : [...tagIds, draggedItem.id], pointIds: [] };
          });
        }
        return [
          ...targets,
          {
            dimensionId: draggedItem.dimId,
            mode: draggedItem.type === "tag" ? "tags" as const : "points" as const,
            tagIds: draggedItem.type === "tag" ? [draggedItem.id] : [],
            pointIds: draggedItem.type === "point" ? [draggedItem.id] : [],
          },
        ];
      };

      // Ein einziger onUpdate-Aufruf für beide Felder gleichzeitig
      onUpdate(creation.id, {
        [otherField]: computeRemoved(creation[otherField]),
        [field]: computeAdded(creation[field]),
      });
    };

    return (
      <div style={{ marginBottom: 12 }}>
        <div
          onDragOver={(e) => {
            const hasOwn = e.dataTransfer.types.includes(`application/x-creation-${field}-delete`);
            const hasForeign = e.dataTransfer.types.includes(`application/x-creation-${otherField}-delete`);
            if (e.dataTransfer.types.includes("application/x-zeitgeist") || hasOwn || hasForeign) {
              e.preventDefault();
              e.currentTarget.style.borderColor = "#4ade80";
              e.currentTarget.style.background = mode === "dark" ? "rgba(74, 222, 128, 0.12)" : "rgba(74, 222, 128, 0.08)";
            }
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
            e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
          }}
          onDrop={(e) => {
            e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
            e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
            // Reorder innerhalb derselben Zone – Pills behandeln das selbst
            if (e.dataTransfer.types.includes(`application/x-creation-${field}-delete`)) {
              e.preventDefault();
              e.stopPropagation();
              droppedInZoneRef.current = true;
              return;
            }
            // Cross-Zone: Item von otherField hierher verschieben
            const crossRaw = e.dataTransfer.getData(`application/x-creation-${otherField}-delete`);
            if (crossRaw) {
              e.preventDefault();
              e.stopPropagation();
              droppedInZoneRef.current = true;
              try {
                const dragged: FlatItem & { kind: string; field: string } = JSON.parse(crossRaw);
                if (dragged.kind === "creation-reorder" && dragged.field === otherField) {
                  moveToThisField(dragged);
                }
              } catch { /* ignore */ }
              return;
            }            // Drag aus dem Dimension-Panel (externe Quelle)
            const data = e.dataTransfer.getData("application/x-zeitgeist");
            if (!data) return;
            e.preventDefault();
            e.stopPropagation();
            try {
              const payload = JSON.parse(data);
              if (payload.kind === "create-condition-from-dimension-point") addToTarget(field, payload.dimensionId, "point", payload.pointId);
              else if (payload.kind === "create-condition-from-dimension-tag") addToTarget(field, payload.dimensionId, "tag", payload.tagId);
              else if (payload.kind === "create-condition-from-dimension") addDimension(field, payload.dimensionId);
            } catch { /* ignore */ }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 10px",
            minHeight: 46,
            border: mode === "dark" ? "1px dashed rgba(255,255,255,0.2)" : "1px dashed rgba(0,0,0,0.15)",
            borderRadius: 8,
            background: mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
            transition: "all 0.15s ease",
            position: "relative",
            flexWrap: "wrap",
          }}
        >
          <span style={{
            fontSize: "0.7em",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)",
            minWidth: 42,
            flexShrink: 0,
            marginRight: 6,
          }}>
            {label}
          </span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
            {items.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                draggable
                onDragStart={(e) => {
                  droppedInZoneRef.current = false;
                  // Globaler dragover-Handler: Browser akzeptiert Drop überall →
                  // onDragEnd feuert sofort ohne macOS-Verzögerungsanimation
                  const handler = (ev: DragEvent) => { ev.preventDefault(); };
                  globalDragOverRef.current = handler;
                  document.addEventListener("dragover", handler);
                  const payload = { type: item.type, id: item.id, dimId: item.dimId, name: item.name, kind: "creation-reorder", field };
                  e.dataTransfer.setData(`application/x-creation-${field}-delete`, JSON.stringify(payload));
                  e.dataTransfer.setData("text/plain", JSON.stringify(payload));
                  e.dataTransfer.effectAllowed = "move";
                  (e.currentTarget as HTMLElement).style.opacity = "0.4";
                }}
                onDragEnd={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "1";
                  if (globalDragOverRef.current) {
                    document.removeEventListener("dragover", globalDragOverRef.current);
                    globalDragOverRef.current = null;
                  }
                  if (!droppedInZoneRef.current) removeItem(field, item);
                }}
                onDragOver={(e) => {
                  const hasOwn = e.dataTransfer.types.includes(`application/x-creation-${field}-delete`);
                  const hasForeign = e.dataTransfer.types.includes(`application/x-creation-${otherField}-delete`);
                  if (hasOwn || hasForeign) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = "move";
                    (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                    (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                  }
                }}
                onDragLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                }}
                onDrop={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                  droppedInZoneRef.current = true;
                  // Cross-Zone: Item von otherField auf dieses Pill droppen → verschieben
                  const crossRaw = e.dataTransfer.getData(`application/x-creation-${otherField}-delete`);
                  if (crossRaw) {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      const dragged: FlatItem & { kind: string; field: string } = JSON.parse(crossRaw);
                      if (dragged.kind === "creation-reorder" && dragged.field === otherField) {
                        moveToThisField(dragged);
                      }
                    } catch { /* ignore */ }
                    return;
                  }
                  // Reorder innerhalb derselben Zone
                  const raw = e.dataTransfer.getData(`application/x-creation-${field}-delete`) || e.dataTransfer.getData("text/plain");
                  if (!raw) return;
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const dragged = JSON.parse(raw);
                    if (dragged.kind !== "creation-reorder" || dragged.field !== field) return;
                    if (dragged.type === item.type && dragged.id === item.id) return;
                    reorderItem(field, dragged, item);
                  } catch { /* ignore */ }
                }}
                style={pillStyle}
              >
                {item.name}
              </div>
            ))}
            {items.length === 0 && (
              <span style={{ color: "rgba(128,128,128,0.5)", fontStyle: "italic", fontSize: "0.9em" }}>
                Drag points here
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={(el) => { setNodeRef(el); (cardRef as any).current = el; }}
      style={{
        ...cardStyle,
        margin: "8px 0",
        padding: 8,
        borderRadius: 6,
        color: "inherit",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
      {...attributes}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div {...listeners} style={{ cursor: "grab" }} title="Drag to reorder · Drag out to remove">☰</div>
        <span style={{ cursor: "pointer", padding: "0 6px", userSelect: "none" }} onClick={() => setExpandedId(expanded ? null : creation.id)}>
          {expanded ? "▾" : "▸"}
        </span>
        {isEditingName ? (
          <input
            type="text"
            value={creation.name}
            onChange={(e) => {
              const stripped = stripSpecialChars(e.target.value);
              onUpdate(creation.id, { name: stripped });
            }}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setIsEditingName(false);
            }}
            autoFocus
            className={`dimension-card__input ${isDuplicate ? "dimension-card__input--duplicate" : ""}`}
            style={{ flex: 1 }}
          />
        ) : (
          <div style={{ flex: 1, userSelect: "none" }} onDoubleClick={() => setIsEditingName(true)}>
            {creation.name}
          </div>
        )}
        {/* Syntax Validation Warning */}
        {errorMessage && (
          <span
            title={errorMessage}
            style={{ fontSize: "1.2em", cursor: "help", animation: "pulse 2s infinite" }}
          >
            {validationIcon}
          </span>
        )}
        {/* Fulfillment-Indikator */}
        {indicator && (
          <span
            ref={indicatorRef}
            style={{ fontSize: "0.8em", fontWeight: "bold", color: indicator.color, cursor: "default", display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2, flexShrink: 0, marginLeft: "auto" }}
            onMouseEnter={() => {
              if (fulfillmentStat?.violations && fulfillmentStat.violations.length > 0) {
                setIsHoveringFulfillment(true);
              }
            }}
            onMouseLeave={() => setIsHoveringFulfillment(false)}
          >
            <span>{indicator.emoji}</span>
            <span>{indicator.pct}</span>
            {/* Violations Tooltip via Portal */}
            {isHoveringFulfillment && fulfillmentStat?.violations && fulfillmentStat.violations.length > 0 && indicatorRef.current && createPortal(
              <div
                style={{
                  position: "fixed",
                  top: indicatorRef.current.getBoundingClientRect().bottom + 8,
                  right: cardRef.current
                    ? document.body.clientWidth - cardRef.current.getBoundingClientRect().right
                    : document.body.clientWidth - indicatorRef.current.getBoundingClientRect().right,
                  maxWidth: cardRef.current ? cardRef.current.getBoundingClientRect().width - 10 : 300,
                  minWidth: 200,
                  background: "linear-gradient(to bottom, var(--pane-bg) 0%, color-mix(in srgb, var(--pane-bg) 95%, black) 100%)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  border: "1px solid color-mix(in srgb, var(--pane-border) 60%, transparent)",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.15)",
                  borderRadius: 10,
                  zIndex: 9999,
                  padding: "4px 0",
                  color: "var(--text-color)",
                  textAlign: "left",
                  pointerEvents: "none",
                }}
              >
                <div style={{ fontWeight: 600, padding: "8px 14px 4px", fontSize: "0.95em", opacity: 0.9, borderBottom: "1px solid rgba(128,128,128,0.1)" }}>
                  {fulfillmentStat.violations.length} Violation{fulfillmentStat.violations.length !== 1 ? "s" : ""}
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                  {fulfillmentStat.violations.map((v: any, i: number) => {
                    const filterText = v.where ? Object.values(v.where).join(", ") : "Global";
                    return (
                      <div
                        key={i}
                        className="cell-dropdown-item"
                        style={{ margin: "2px 6px", cursor: "default", fontSize: "0.9em", animationDelay: `${i * 0.05}s`, pointerEvents: "auto" }}
                      >
                        {filterText}
                      </div>
                    );
                  })}
                </div>
              </div>,
              document.body
            )}
          </span>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 12, width: "100%", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap", width: "100%", boxSizing: "border-box" }}>
            <select
              value={creation.type}
              onChange={(e) => onUpdate(creation.id, { type: e.target.value as Creation["type"] })}
              style={{ padding: "6px 10px", borderRadius: 6, opacity: 0.8, background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)", color: "inherit", border: "1px solid rgba(128,128,128,0.3)", minWidth: 0 }}
            >
              <option value="equal">Equal</option>
              <option value="maximize">Maximize</option>
            </select>
            <input
              type="number"
              value={creation.equalValue}
              onChange={(e) => onUpdate(creation.id, { equalValue: parseInt(e.target.value) || 0 })}
              style={{ width: 80, maxWidth: "100%", padding: "6px 10px", borderRadius: 6, border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)", background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)", color: "inherit", boxSizing: "border-box" }}
            />
            <label style={{ fontWeight: 500, fontSize: "0.95em" }}>Enforcement:</label>
            <select
              value={creation.enforcement}
              onChange={(e) => onUpdate(creation.id, { enforcement: e.target.value as Creation["enforcement"] })}
              style={{ padding: "6px 10px", borderRadius: 6, background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)", color: "inherit", border: "1px solid rgba(128,128,128,0.3)", minWidth: 0 }}
            >
              <option value="hard">hard</option>
              <option value="off">off</option>
            </select>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, userSelect: "none" }}>
              <input
                type="checkbox"
                checked={creation.connect}
                onChange={(e) => onUpdate(creation.id, { connect: e.target.checked })}
              />
              Connect
            </label>
          </div>
          <DropZone label="MAKE" field="make" items={makeItems} />
          <DropZone label="FOR" field="for" items={forItems} />
          {/* Validation Error Banner */}
          {errorMessage && (
            <div className="syntax-error-banner">
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CreationEditor({ creations, onChange, dimensions, allNames, isActive, toggle, userHue, mode, projectId, syntaxData, statsData }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeCreationId, setActiveCreationId] = useState<string | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);

  const creationSyntaxMap = useMemo(() => {
    const map = new Map<string, { syntax?: string; SyntaxMessages: string[] }>();
    const arr = Array.isArray(syntaxData) ? syntaxData : [];
    arr.forEach((entry: any) => {
      const name = entry?.creationName;
      if (name) {
        map.set(name, {
          syntax: entry.syntax,
          SyntaxMessages: entry.message ? [entry.message] : [],
        });
      }
    });
    return map;
  }, [syntaxData]);

  const creationStatsMap = useMemo(() => {
    const map = new Map<string, { Fulfillment: number; violations: any[] }>();
    const arr = Array.isArray(statsData) ? statsData : [];
    arr.forEach((entry: any) => {
      const name = entry?.creationName;
      if (!name) return;
      map.set(name, {
        Fulfillment: entry.fulfillment ?? entry.Fulfillment,
        violations: entry.violations ?? [],
      });
    });
    return map;
  }, [statsData]);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.relatedTarget === null) {
        document.dispatchEvent(new MouseEvent("mouseup", { bubbles: true }));
      }
    };
    window.addEventListener("mouseleave", handleMouseLeave);
    return () => window.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  const handleAdd = () => {
    const newCreation: Creation = {
      id: Date.now().toString(),
      name: `Creation${creations.length + 1}`,
      type: "equal",
      equalValue: 1,
      enforcement: "hard",
      connect: false,
      make: [],
      for: [],
    };
    onChange([...creations, newCreation]);
    setExpandedId(newCreation.id);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleUpdate = (id: string, updates: Partial<Creation>) => {
    onChange(creations.map((creation) => (creation.id === id ? { ...creation, ...updates } : creation)));
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleDelete = (id: string) => {
    onChange(creations.filter((creation) => creation.id !== id));
    if (expandedId === id) setExpandedId(null);
    notifyProjectActivity(projectId).catch(() => {});
  };

  return (
    <div
      className="creation-editor"
      ref={editorFrameRef}
      style={{
        marginTop: "1em",
        marginBottom: "1em",
        padding: "0.5em",
        borderRadius: 8,
        background: getBaseColor(2, "section", userHue, mode),
      }}
    >
      <div
        className="creation-editor__header"
        onClick={toggle}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong className="creation-editor__title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isActive ? "▾" : "▸"} Creations
        </strong>
      </div>
      {isActive && (
        <div className="creation-editor__body" style={{ marginTop: "0.5em" }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => {
              setExpandedId(null);
              setActiveCreationId(String(e.active.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveCreationId(null);

              // Drag-out-to-delete: Element komplett außerhalb des Editors → löschen
              const finalRect = active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (
                finalRect && editorRect &&
                (finalRect.right <= editorRect.left ||
                  finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top ||
                  finalRect.top >= editorRect.bottom)
              ) {
                handleDelete(String(active.id));
                return;
              }

              if (active.id !== over?.id) {
                const oldIndex = creations.findIndex((c) => c.id === active.id);
                const newIndex = creations.findIndex((c) => c.id === over?.id);
                onChange(arrayMove(creations, oldIndex, newIndex));
              }
            }}
          >
            <SortableContext items={creations.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {creations.map((creation) => (
                <SortableCreationCard
                  key={creation.id}
                  creation={creation}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  dimensions={dimensions}
                  userHue={userHue}
                  mode={mode}
                  allNames={allNames}
                  syntaxStat={creationSyntaxMap.get(creation.name)}
                  fulfillmentStat={creationStatsMap.get(creation.name)}
                />
              ))}
            </SortableContext>

            {/* DragOverlay – Ghost-Karte beim Ziehen */}
            <DragOverlay>
              {activeCreationId ? (
                <div
                  className="drag-overlay-card"
                  style={{
                    background: getBaseColor(4, "connection", userHue, mode),
                  }}
                >
                  <div className="editor-card__drag">☰</div>
                  <div style={{ flex: 1 }}>
                    {creations.find((c) => c.id === activeCreationId)?.name ?? ""}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
          {creations.length === 0 && (
            <div style={{ color: mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", fontStyle: "italic", fontSize: "0.9em", padding: "8px 4px" }}>
              No creations defined.
            </div>
          )}
          <button onClick={handleAdd} className="creation-editor__add">
            + Creation
          </button>
        </div>
      )}
    </div>
  );
}
